import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import {
  ESTADO_LABEL,
  ESTADOS_CON_UBICACION,
  UBICACION_LABEL,
  type Dispositivo,
  type Estado,
  type TipoUbicacion,
} from '@/lib/dispositivos';

/**
 * Las transiciones que no son instalar ni retirar: revisado (de cuarentena a
 * stock), a reparación, baja, perdido, aparecido… y cambiar de sitio dentro
 * del stock, que es lo más frecuente («de la oficina a la furgoneta»).
 */
interface Props {
  abierto: boolean;
  dispositivo: Dispositivo;
  transiciones: Estado[];
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
}

const UBICACIONES = Object.keys(UBICACION_LABEL) as TipoUbicacion[];

const VERBO: Partial<Record<Estado, string>> = {
  stock: 'Revisado, vuelve al stock',
  reparacion: 'Mandar a reparar',
  baja: 'Dar de baja',
  perdido: 'Marcar como perdido',
  cuarentena: 'Poner en cuarentena',
};

export default function ModalEstado({ abierto, dispositivo: d, transiciones, onCerrar, onGuardar }: Props) {
  const puedeCambiarSitio = ESTADOS_CON_UBICACION.includes(d.estado);
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const [a, setA] = useState<Estado>(transiciones[0] ?? d.estado);
  const [ubicacionTipo, setUbicacionTipo] = useState<TipoUbicacion>(d.ubicacion?.tipo ?? 'oficina');
  const [ubicacionNombre, setUbicacionNombre] = useState(d.ubicacion?.nombre ?? '');
  const [proveedor, setProveedor] = useState('');
  const [enviadoEl, setEnviadoEl] = useState(hoy);
  const [referencia, setReferencia] = useState('');
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conUbicacion = ESTADOS_CON_UBICACION.includes(a);
  const opciones: Estado[] = puedeCambiarSitio ? [d.estado, ...transiciones] : transiciones;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({
        a,
        ubicacion: conUbicacion ? { tipo: ubicacionTipo, ...(ubicacionNombre.trim() ? { nombre: ubicacionNombre.trim() } : {}) } : undefined,
        reparacion: a === 'reparacion' ? { proveedor: proveedor.trim(), enviadoEl, ...(referencia.trim() ? { referencia: referencia.trim() } : {}) } : undefined,
        nota: nota.trim() || undefined,
      });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Cambiar de estado">
      <form onSubmit={enviar} className="space-y-4">
        <label className="block">
          <span className="label">Pasa a</span>
          <select className="input mt-1" value={a} onChange={(e) => setA(e.target.value as Estado)}>
            {opciones.map((e) => (
              <option key={e} value={e}>
                {e === d.estado ? `Sigue ${ESTADO_LABEL[e].toLowerCase()}, cambia de sitio` : VERBO[e] ?? ESTADO_LABEL[e]}
              </option>
            ))}
          </select>
        </label>

        {conUbicacion && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label">Dónde</span>
              <select className="input mt-1" value={ubicacionTipo} onChange={(e) => setUbicacionTipo(e.target.value as TipoUbicacion)}>
                {UBICACIONES.map((u) => (
                  <option key={u} value={u}>{UBICACION_LABEL[u]}</option>
                ))}
              </select>
            </label>
            {ubicacionTipo === 'con_empleado' && (
              <label className="block">
                <span className="label">Con quién *</span>
                <input className="input mt-1" required value={ubicacionNombre} onChange={(e) => setUbicacionNombre(e.target.value)} />
              </label>
            )}
          </div>
        )}

        {a === 'reparacion' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label">A quién se manda *</span>
              <input className="input mt-1" required value={proveedor} onChange={(e) => setProveedor(e.target.value)} placeholder="Nuki, SwitchBot, tienda…" />
            </label>
            <label className="block">
              <span className="label">Enviado el</span>
              <input type="date" className="input mt-1" value={enviadoEl} onChange={(e) => setEnviadoEl(e.target.value)} />
            </label>
            <label className="block col-span-2">
              <span className="label">Referencia (RMA, ticket…)</span>
              <input className="input mt-1" value={referencia} onChange={(e) => setReferencia(e.target.value)} />
            </label>
          </div>
        )}

        <label className="block">
          <span className="label">Nota{a === 'baja' || a === 'perdido' ? ' *' : ''}</span>
          <textarea className="input mt-1" rows={2} required={a === 'baja' || a === 'perdido'} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>

        {a === 'baja' && <p className="text-xs text-amber-700">La baja es definitiva: no se vuelve a instalar ni aparece en el inventario por defecto.</p>}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
